import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Upload, Palette, Sparkles, Download } from "lucide-react";
import logoWhite from "@/assets/logo-white.png";
import onboardingBg from "@/assets/onboarding-bg.svg";
import { supabase } from "@/integrations/supabase/client";
/**
 * Mobile Onboarding Screen
 * Multi-step onboarding flow for native mobile app
 */

const onboardingSteps = [
  {
    title: "Upload Your Room",
    description: "Take a photo of any room you want to redesign and upload it to get started",
    icon: Upload,
  },
  {
    title: "Choose Your Style",
    description: "Select from 90+ design styles including Modern, Traditional, French, Indian, and more",
    icon: Palette,
  },
  {
    title: "AI Magic",
    description: "Our AI generates 3 unique design variations (1 credit each) with 360° immersive views",
    icon: Sparkles,
  },
  {
    title: "Download & Share",
    description: "Compare designs with before/after slider and download all variations",
    icon: Download,
  },
];

// Custom sparkle SVG for welcome screen
const OutlineSparkle = ({ className }: { className?: string }) => (
  <svg
    width="29"
    height="30"
    viewBox="0 0 29 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path
      d="M18.4706 27.25C20.3765 20.8135 22.4825 18.4913 28 16.75C22.4825 15.0087 20.3765 12.6865 18.4706 6.25C16.5647 12.6865 14.4587 15.0087 8.94118 16.75C14.4587 18.4913 16.5647 20.8135 18.4706 27.25ZM5.76471 11.5C6.71765 8.28 7.77065 7.11975 10.5294 6.25C7.77065 5.38025 6.71765 4.22 5.76471 1C4.81176 4.22 3.75876 5.38025 1 6.25C3.75876 7.11975 4.81176 8.28 5.76471 11.5ZM8.14706 29C8.62353 27.39 9.14924 26.8107 10.5294 26.375C9.14924 25.9393 8.62353 25.36 8.14706 23.75C7.67059 25.36 7.14488 25.9393 5.76471 26.375C7.14488 26.8107 7.67059 27.39 8.14706 29Z"
      stroke="#E6E6E6"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

// SVG Illustration components for each step
const UploadIllustration = () => (
  <div className="flex flex-col items-center justify-center h-48 relative">
    <svg viewBox="0 0 200 150" className="w-48 h-36">
      {/* Room outline */}
      <rect x="30" y="30" width="140" height="100" rx="8" fill="#F5F0E8" stroke="#D4C5B5" strokeWidth="2"/>
      {/* Window */}
      <rect x="50" y="45" width="40" height="35" rx="2" fill="#E8F4FC" stroke="#B8D4E8" strokeWidth="1.5"/>
      <line x1="70" y1="45" x2="70" y2="80" stroke="#B8D4E8" strokeWidth="1"/>
      <line x1="50" y1="62" x2="90" y2="62" stroke="#B8D4E8" strokeWidth="1"/>
      {/* Sofa */}
      <rect x="55" y="95" width="55" height="25" rx="4" fill="#8B7355"/>
      <rect x="50" y="90" width="65" height="12" rx="4" fill="#A08060"/>
      {/* Plant */}
      <ellipse cx="140" cy="110" rx="12" ry="8" fill="#4A7C59"/>
      <rect x="135" y="112" width="10" height="12" rx="2" fill="#C4956A"/>
      {/* Camera frame */}
      <rect x="120" y="10" width="70" height="55" rx="8" fill="#2D2D2D" stroke="#1A1A1A" strokeWidth="2"/>
      <circle cx="155" cy="35" r="15" fill="#1A1A1A"/>
      <circle cx="155" cy="35" r="10" fill="#3D5A80"/>
      <circle cx="155" cy="35" r="4" fill="#1A1A1A"/>
      {/* Upload arrow */}
      <circle cx="155" cy="35" r="20" fill="none" stroke="#4A5D4F" strokeWidth="2" strokeDasharray="4 2"/>
      <path d="M155 25 L155 45 M148 32 L155 25 L162 32" stroke="#4A5D4F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </div>
);

const StyleIllustration = () => (
  <div className="flex flex-col items-center justify-center h-48">
    <svg viewBox="0 0 200 140" className="w-52 h-40">
      {/* Style swatches */}
      <rect x="10" y="10" width="55" height="55" rx="8" fill="#F5E6D3" stroke="#E8D4C0" strokeWidth="2"/>
      <text x="37" y="42" textAnchor="middle" fontSize="7" fill="#8B7355" fontWeight="600">Modern</text>
      
      <rect x="72" y="10" width="55" height="55" rx="8" fill="#E8F0E8" stroke="#C5D8C5" strokeWidth="2"/>
      <text x="100" y="42" textAnchor="middle" fontSize="7" fill="#4A7C59" fontWeight="600">Minimal</text>
      
      <rect x="134" y="10" width="55" height="55" rx="8" fill="#FFF0E8" stroke="#FFD8C8" strokeWidth="2"/>
      <text x="162" y="42" textAnchor="middle" fontSize="7" fill="#C4956A" fontWeight="600">Boho</text>
      
      <rect x="10" y="72" width="55" height="55" rx="8" fill="#E8E0F0" stroke="#D0C0E0" strokeWidth="2"/>
      <text x="37" y="104" textAnchor="middle" fontSize="7" fill="#7C6A99" fontWeight="600">French</text>
      
      <rect x="72" y="72" width="55" height="55" rx="8" fill="#FFE8E0" stroke="#FFC8B8" strokeWidth="2"/>
      <text x="100" y="104" textAnchor="middle" fontSize="7" fill="#B87C60" fontWeight="600">Indian</text>
      
      <rect x="134" y="72" width="55" height="55" rx="8" fill="#E0F0F8" stroke="#B8D8E8" strokeWidth="2"/>
      <text x="162" y="104" textAnchor="middle" fontSize="7" fill="#5A8CA8" fontWeight="600">Coastal</text>
      
      {/* Palette icon */}
      <circle cx="100" cy="70" r="16" fill="#4A5D4F"/>
      <circle cx="94" cy="64" r="3" fill="#F5E6D3"/>
      <circle cx="106" cy="64" r="3" fill="#E8F0E8"/>
      <circle cx="94" cy="76" r="3" fill="#FFF0E8"/>
      <circle cx="106" cy="76" r="3" fill="#E8E0F0"/>
    </svg>
    <div className="text-xs text-gray-500 mt-1 font-medium">90+ Design Styles</div>
  </div>
);

const MagicIllustration = () => (
  <div className="flex flex-col items-center justify-center h-48 relative">
    <svg viewBox="0 0 200 130" className="w-52 h-36">
      {/* Original room (left) */}
      <rect x="5" y="25" width="60" height="45" rx="4" fill="#E8E0D8" stroke="#C8BFB5" strokeWidth="1.5"/>
      <rect x="15" y="35" width="20" height="15" rx="1" fill="#D8D0C8"/>
      <rect x="20" y="55" width="30" height="10" rx="2" fill="#A89888"/>
      
      {/* Magic wand / sparkles in center */}
      <circle cx="100" cy="50" r="20" fill="#4A5D4F" opacity="0.1"/>
      <path d="M95 35 L100 50 L105 35 L100 30 Z" fill="#FFD700"/>
      <path d="M85 45 L100 50 L85 55 L80 50 Z" fill="#FFD700"/>
      <path d="M115 45 L100 50 L115 55 L120 50 Z" fill="#FFD700"/>
      <path d="M95 65 L100 50 L105 65 L100 70 Z" fill="#FFD700"/>
      <circle cx="100" cy="50" r="8" fill="#4A5D4F"/>
      <Sparkles className="w-4 h-4 text-white" />
      
      {/* Transformed room (right) */}
      <rect x="135" y="25" width="60" height="45" rx="4" fill="#F5EDE5" stroke="#E0D5C8" strokeWidth="1.5"/>
      <rect x="145" y="32" width="25" height="18" rx="2" fill="#E8F4FC" stroke="#B8D4E8" strokeWidth="0.5"/>
      <rect x="150" y="52" width="35" height="12" rx="3" fill="#A08060"/>
      <ellipse cx="178" cy="60" rx="6" ry="5" fill="#4A7C59"/>
      
      {/* Arrow */}
      <path d="M70 50 L125 50" stroke="#4A5D4F" strokeWidth="2" strokeDasharray="4 2"/>
      <path d="M120 45 L130 50 L120 55" fill="#4A5D4F"/>
      
      {/* 3 variation thumbnails */}
      <rect x="40" y="85" width="35" height="28" rx="3" fill="#FAF6F0" stroke="#E0D5C8" strokeWidth="1"/>
      <text x="57" y="103" textAnchor="middle" fontSize="8" fill="#8B7355">1</text>
      
      <rect x="82" y="85" width="35" height="28" rx="3" fill="#FAF6F0" stroke="#4A5D4F" strokeWidth="2"/>
      <text x="100" y="103" textAnchor="middle" fontSize="8" fill="#4A5D4F" fontWeight="bold">2</text>
      
      <rect x="124" y="85" width="35" height="28" rx="3" fill="#FAF6F0" stroke="#E0D5C8" strokeWidth="1"/>
      <text x="142" y="103" textAnchor="middle" fontSize="8" fill="#8B7355">3</text>
    </svg>
    <div className="text-xs text-gray-500 mt-1 font-medium">3 AI-Generated Variations</div>
  </div>
);

const ShareIllustration = () => (
  <div className="flex flex-col items-center justify-center h-48 relative">
    <svg viewBox="0 0 200 130" className="w-52 h-36">
      {/* Before image */}
      <rect x="15" y="15" width="75" height="60" rx="6" fill="#E0D8D0" stroke="#C8C0B8" strokeWidth="1.5"/>
      <rect x="25" y="25" width="25" height="18" rx="1" fill="#D0C8C0"/>
      <rect x="25" y="48" width="50" height="15" rx="2" fill="#B8A898"/>
      <text x="52" y="85" textAnchor="middle" fontSize="9" fill="#888" fontWeight="500">Before</text>
      
      {/* Comparison slider */}
      <line x1="100" y1="10" x2="100" y2="80" stroke="#4A5D4F" strokeWidth="3"/>
      <circle cx="100" cy="45" r="10" fill="white" stroke="#4A5D4F" strokeWidth="2"/>
      <path d="M96 42 L93 45 L96 48" stroke="#4A5D4F" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      <path d="M104 42 L107 45 L104 48" stroke="#4A5D4F" strokeWidth="1.5" fill="none" strokeLinecap="round"/>
      
      {/* After image */}
      <rect x="110" y="15" width="75" height="60" rx="6" fill="#F5EDE5" stroke="#E8DDD0" strokeWidth="1.5"/>
      <rect x="120" y="22" width="28" height="20" rx="2" fill="#E8F4FC" stroke="#B8D4E8" strokeWidth="0.5"/>
      <rect x="120" y="48" width="50" height="15" rx="3" fill="#A08060"/>
      <ellipse cx="170" cy="58" rx="8" ry="6" fill="#4A7C59"/>
      <text x="147" y="85" textAnchor="middle" fontSize="9" fill="#4A5D4F" fontWeight="500">After</text>
      
      {/* Action buttons */}
      <circle cx="60" cy="110" r="14" fill="#4A5D4F"/>
      <path d="M55 110 L60 115 L65 110 M60 105 L60 115" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      
      <circle cx="100" cy="110" r="14" fill="#E8E0D8" stroke="#C8BFB5" strokeWidth="1"/>
      <circle cx="100" cy="107" r="4" fill="#8B7355"/>
      <path d="M93 113 Q100 118 107 113" stroke="#8B7355" strokeWidth="1.5" fill="none"/>
      
      <circle cx="140" cy="110" r="14" fill="#E8E0D8" stroke="#C8BFB5" strokeWidth="1"/>
      <path d="M135 107 L140 110 L145 107 M135 113 L140 116 L145 113" stroke="#5A8CA8" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
    <div className="text-xs text-gray-500 mt-1 font-medium">Compare, Download & Share</div>
  </div>
);

const illustrations = [
  UploadIllustration,
  StyleIllustration,
  MagicIllustration,
  ShareIllustration,
];

const Onboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [checkingAuth, setCheckingAuth] = useState(true);
  
  // Check if user is already logged in
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        navigate("/designer", { replace: true });
      } else if (!navigator.onLine) {
        // Offline with no cached session — stay on onboarding, don't loop
        setCheckingAuth(false);
      } else {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [navigate]);
  
  // Touch/swipe handling
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    const x = e.touches[0]?.clientX;
    if (typeof x !== "number") return;
    touchStartX.current = x;
    // Initialize end to start so taps don't look like massive swipes
    touchEndX.current = x;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    const x = e.touches[0]?.clientX;
    if (typeof x !== "number") return;
    touchEndX.current = x;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current == null || touchEndX.current == null) return;

    const swipeDistance = touchStartX.current - touchEndX.current;

    // Ignore taps / tiny drags
    if (Math.abs(swipeDistance) < minSwipeDistance) {
      touchStartX.current = null;
      touchEndX.current = null;
      return;
    }

    // Only handle swipes when in feature content (steps 1-4)
    setCurrentStep((prev) => {
      if (prev > 0) {
        if (swipeDistance > 0 && prev < 4) {
          // Swipe left = go to next step
          return prev + 1;
        }
        if (swipeDistance < 0 && prev > 1) {
          // Swipe right = go to previous step
          return prev - 1;
        }
      }
      return prev;
    });

    // Reset
    touchStartX.current = null;
    touchEndX.current = null;
  };

  const handleNext = () => {
    setCurrentStep(prev => {
      if (prev < 4) {
        return prev + 1;
      } else {
        navigate("/auth");
        return prev;
      }
    });
  };

  const handleSkip = () => {
    navigate("/auth");
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#4A5D4F]">
        <div className="animate-pulse">
          <img src={logoWhite} alt="Decor Canvas" className="w-24 h-24" />
        </div>
      </div>
    );
  }

  // Welcome screen (Step 0)
  if (currentStep === 0) {
    return (
      <div className="relative min-h-screen w-full overflow-hidden">
        {/* Background Image */}
        <img
          src={onboardingBg}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col min-h-screen p-6 pb-10">
          {/* Logo */}
          <img
            src={logoWhite}
            alt="Decor Canvas"
            className="w-28 h-28 -ml-4 mt-10"
          />

          {/* Headline */}
          <div className="flex-1 flex items-start">
            <h1 className="text-5xl font-bold text-white leading-tight tracking-tight">
              Transform
              <br />
              Your Space
              <br />
              with AI
              <OutlineSparkle className="inline w-7 h-7 ml-1 align-middle mb-10" />
            </h1>
          </div>

          {/* CTA Button */}
          <div className="flex justify-center">
            <Button
              onClick={handleNext}
              size="sm"
              className="w-56 mb-20 bg-white text-gray-900 hover:bg-gray-100 p-5 text-sm font-bold rounded-full shadow-lg"
            >
              Start Designing Now
              <ArrowRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Feature screens (Steps 1-4)
  const stepIndex = currentStep - 1;
  const stepData = onboardingSteps[stepIndex];
  const IllustrationComponent = illustrations[stepIndex];
  const isLastStep = currentStep === 4;

  return (
    <div 
      className="relative min-h-screen w-full overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Image */}
      <img
        src={onboardingBg}
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Content */}
      <div className="relative z-10 flex flex-col min-h-screen p-6">
        {/* Logo */}
        <img
          src={logoWhite}
          alt="Decor Canvas"
          className="w-20 h-20 -ml-2 mt-6"
        />

        {/* Feature Card */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm bg-[#FAF6F0] rounded-3xl p-6 shadow-xl">
            {/* Illustration */}
            <IllustrationComponent />

            {/* Title */}
            <h2 className="text-xl font-bold text-gray-900 text-center mt-4">
              {stepData.title}
            </h2>

            {/* Description */}
            <p className="text-sm text-gray-600 text-center mt-2 leading-relaxed">
              {stepData.description}
            </p>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {[1, 2, 3, 4].map((dot) => (
                <div
                  key={dot}
                  className={`w-2 h-2 rounded-full transition-all ${
                    dot === currentStep
                      ? "bg-gray-800 w-4"
                      : "bg-gray-300"
                  }`}
                />
              ))}
            </div>

            {/* Navigation */}
            <div className={`flex items-center mt-6 ${isLastStep ? 'justify-center' : 'justify-between'}`}>
              {!isLastStep && (
                <button
                  onClick={handleSkip}
                  className="text-sm text-gray-800 font-medium hover:text-gray-700 transition-colors"
                >
                  Skip
                </button>
              )}

              <Button
                onClick={isLastStep ? () => navigate("/auth") : handleNext}
                className={`bg-[#E38D5B] hover:bg-[#E38D5B]/90 text-black rounded-full px-6 py-2 text-sm font-medium ${isLastStep ? 'w-full' : ''}`}
              >
                {isLastStep ? "Sign In" : "Next"}
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
